// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useInstruments, UseInstrumentsResultGetByKey } from '@hooks/other/useInstruments';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import TemplateContract from '../../../../utils/TemplateContract';
import { createInstrumentKey, Instrument, InstrumentKey } from '../../../../utils/instrument';
import { Position } from '../../types';

const emptyPosition = (uuid: string): Position => ({
  admin: '',
  id: '',
  amount: '',
  uuid,
});

const getEligibleInstruments = (
  getInstrumentByKey: UseInstrumentsResultGetByKey,
  collateralAgreement?: TemplateContract<CollateralAgreement>,
) => {
  const all: Instrument[] = [];
  const byKey = new Map<InstrumentKey, Instrument>();

  const result = { all, byKey };

  if (!collateralAgreement) {
    return result;
  }

  collateralAgreement.payload.terms.eligibleCollateral.eligibleInstruments.forEach((inst) => {
    const instrumentKey = createInstrumentKey(inst.instrument.id, inst.instrument.admin);
    const instrument = getInstrumentByKey(instrumentKey);

    if (!instrument) {
      console.warn(`Failed to get instrument for key: "${instrumentKey}".`);
      return;
    }

    all.push(instrument);
    byKey.set(instrumentKey, instrument);
  });

  return result;
};

export const usePositions = (collateralAgreement?: TemplateContract<CollateralAgreement>) => {
  const { getInstrumentByKey } = useInstruments();

  const [positionsMap, setPositionsMap] = useState<Map<string, Position>>(
    new Map<string, Position>(),
  );

  const positions = useMemo(() => {
    return Array.from(positionsMap.values());
  }, [positionsMap]);

  // --- ACCESS & MANIPULATION ---
  const handlers = useMemo(() => {
    const eligibleInstruments = getEligibleInstruments(getInstrumentByKey, collateralAgreement);

    return {
      eligibleInstruments,
      addPosition: () => {
        setPositionsMap((map) => {
          const uuid = uuidv4();
          map.set(uuid, emptyPosition(uuid));

          return new Map(map);
        });
      },
      updatePositionInstrument: (uuid: string, instrumentKey: InstrumentKey) => {
        setPositionsMap((map) => {
          const position = map.get(uuid) ?? emptyPosition(uuid);
          const instrument = eligibleInstruments.byKey.get(instrumentKey);

          if (!instrument) {
            console.warn('Tried to update position with invalid instrument key:', instrumentKey);
            return map;
          }

          map.set(uuid, {
            ...position,
            id: instrument.id,
            admin: instrument.admin,
          });

          return new Map(map);
        });
      },
      updatePositionAmount: (uuid: string, amount: string) => {
        setPositionsMap((map) => {
          const instrument = map.get(uuid);

          if (!instrument) {
            console.warn(`Tried to update non-existing eligible instrument with uuid ${uuid}.`);
            return map;
          }

          map.set(uuid, { ...instrument, amount });

          return new Map(map);
        });
      },
      removePosition: (uuid: string) => {
        setPositionsMap((map) => {
          map.delete(uuid);

          return new Map(map);
        });
      },
      resetPositions: () => {
        setPositionsMap(new Map<string, Position>());
      },
    };
  }, [collateralAgreement, getInstrumentByKey]);

  // If form allows to change collateral agreement dynamically, we can use this method to handle
  // positions change gracefully.
  useEffect(
    (newCollateralAgreement?: TemplateContract<CollateralAgreement>) => {
      setPositionsMap((map) => {
        if (!newCollateralAgreement) {
          const uuid = uuidv4();
          return new Map([[uuid, emptyPosition(uuid)]]);
        }

        const agreementEligibleInstruments = new Set(
          newCollateralAgreement.payload.terms.eligibleCollateral.eligibleInstruments.map(
            ({ instrument: { id, admin } }) => createInstrumentKey(id, admin),
          ),
        );

        if (
          map.size > 0 &&
          Array.from(map.values()).every(({ id, admin }) =>
            agreementEligibleInstruments.has(createInstrumentKey(id, admin)),
          )
        ) {
          return map;
        }

        if (
          newCollateralAgreement.payload.terms.eligibleCollateral.eligibleInstruments.length === 1
        ) {
          const { instrument } =
            newCollateralAgreement.payload.terms.eligibleCollateral.eligibleInstruments[0];
          const uuid = uuidv4();
          return new Map([
            [
              uuid,
              {
                admin: instrument.admin,
                id: instrument.id,
                amount: '',
                uuid,
              },
            ],
          ]);
        }

        const uuid = uuidv4();
        return new Map([[uuid, emptyPosition(uuid)]]);
      });
    },
    [collateralAgreement],
  );

  return {
    positions,
    ...handlers,
  };
};
